import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/entreprise/Chauffeur-pour-entreprise.png";
import { cityPriceFaq } from "./cityPricing";

const biot: ChauffeurPage = {
  slug: "biot",
  title: "Chauffeur privé à Biot – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Biot : transferts aéroport de Nice, village perché, verrerie et secteur Marineland, 7j/7.",
  keywords: [
    "chauffeur privé biot",
    "vtc biot",
    "chauffeur biot",
    "vtc biot marineland",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Biot",
    subtitle:
      "Transferts aéroport de Nice, village perché et verrerie, secteur Marineland et gare de Biot — 24h/24, 7j/7.",
    keywords: [
      "VTC Biot",
      "chauffeur village de Biot",
      "transfert aéroport Nice",
      "chauffeur Marineland",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Biot, village et verrerie",
    badges: ["Village & verrerie", "Suivi de vol", "Chauffeurs bilingues"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Biot",
    items: [
      {
        title: "Village perché & artisans",
        description:
          "Verrerie de Biot au verre bullé, potiers et galeries : dépose au pied du village médiéval, sans souci de stationnement.",
      },
      {
        title: "Secteur Marineland & bord de mer",
        description:
          "Gare de Biot, plages et abords du littoral : dépose et reprise aisées côté mer, à l’écart des ruelles.",
      },
      {
        title: "Aéroport de Nice en ~20 min",
        description:
          "Liaison directe par l’A8 avec suivi de vol et accueil personnalisé, dans les deux sens.",
      },
      {
        title: "Ancrage local",
        description:
          "Basés sur la Côte d’Azur, note 5,0/5 sur 38 avis Google, chauffeurs bilingues FR/EN.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Biot",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Biot ⇄ aéroport de Nice Côte d’Azur en ~20 min par l’A8, avec suivi de vol et accueil aux terminaux.",
      },
      {
        title: "Village & verrerie",
        text: "Dépose au pied du village perché, attente pendant la visite de la verrerie et des galeries, reprise sans contrainte.",
      },
      {
        title: "Musée national Fernand Léger",
        text: "Liaison vers le musée et ses jardins, avec attente sur place ou retour à l’hôtel.",
      },
      {
        title: "Secteur Marineland & plages",
        text: "Dessertes du secteur de Marineland, de la gare de Biot et des plages, pratiques pour les familles.",
      },
      {
        title: "Sophia Antipolis côté Biot",
        text: "Accueil de cadres et rendez-vous d’affaires sur la partie de la technopole côté Biot, facturation entreprise.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      {
        name: "Village de Biot",
        description:
          "Village médiéval perché, place des Arcades et ruelles piétonnes.",
      },
      {
        name: "Verrerie de Biot",
        description: "Atelier du verre bullé et boutiques d’artisans.",
      },
      {
        name: "Musée national Fernand Léger",
        description:
          "Musée et jardins dédiés au peintre, en contrebas du village.",
      },
      {
        name: "Gare de Biot (secteur Marineland)",
        description: "Bord de mer, plages et pôle du littoral.",
      },
      {
        name: "Sophia Antipolis (côté Biot)",
        description:
          "Écoles, entreprises et parcs d’activités de la technopole.",
      },
      { name: "Aéroport de Nice", description: "~20 min par l’A8." },
    ],
  },
  generic: {
    heading: "Biot, village du verre entre collines et littoral",
    subheading:
      "Du village perché des artisans au secteur Marineland en bord de mer, un chauffeur pour toute la commune.",
    paragraphs: [
      "Biot est d’abord un village médiéval perché, célèbre pour sa verrerie au verre bullé, ses potiers et ses galeries, groupés autour de la place des Arcades. Les ruelles y sont piétonnes et le stationnement rare : le chauffeur vous dépose au pied du village et vous reprend sur simple demande.",
      "En contrebas, la commune descend jusqu’au littoral : la gare de Biot, le secteur de Marineland et les plages forment un pôle animé, à quelques minutes du village par la route. Nous relions facilement les deux, sans que vous ayez à chercher une place côté mer.",
      "Biot accueille aussi une partie de Sophia Antipolis, avec écoles et entreprises : pour ces déplacements d’affaires, nous assurons l’accueil à l’aéroport de Nice — à ~20 min par l’A8 — et les rendez-vous, avec facturation entreprise. Le Musée national Fernand Léger complète la visite culturelle.",
    ],
    bullets: [
      "Dépose au pied du village",
      "Secteur Marineland & plages",
      "Verrerie, galeries & Musée Léger",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Biot",
    steps: [
      "Indiquez votre trajet ou vos adresses (aéroport, village, verrerie, secteur Marineland).",
      "Nous confirmons le véhicule adapté et le forfait ou la mise à disposition.",
      "Votre chauffeur suit votre vol et vous accueille à l’heure.",
      "Dépose au pied du village ou côté mer, reprise sur demande.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Biot"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice depuis Biot ?",
      a: "Comptez environ 20 minutes par l’A8 hors heures de pointe. Nous suivons votre vol pour ajuster l’heure de prise en charge, dans les deux sens.",
    },
    {
      q: "Desservez-vous le secteur de Marineland et les plages avec des enfants ?",
      a: "Oui. Nous déposons et reprenons dans le secteur de Marineland, à la gare de Biot et le long des plages, avec sièges enfants sur demande — pratique pour les sorties en famille sans souci de stationnement.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et les week-ends d’affluence ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à une clientèle internationale.",
    },
    {
      q: "Quels véhicules proposez-vous à Biot ?",
      a: "Berline Mercedes Classe E et Classe S, van 7 places, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
    {
      q: "Peut-on accéder au village de Biot en voiture ?",
      a: "Le cœur du village perché est piétonnier. Le chauffeur vous dépose au plus près, au pied du village, puis vous reprend au même endroit après la visite de la verrerie et des galeries.",
    },
  ],
  relatedLinks: [
    {
      label: "Mise à disposition avec chauffeur à Sophia Antipolis",
      href: "/chauffeur/sophia-antipolis",
    },
    { label: "Navette aéroport de Nice", href: "/chauffeur/aeroport" },
    { label: "Chauffeur Nice, tarifs et trajets", href: "/chauffeur/nice" },
    {
      label: "VTC Côte d’Azur",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Biot",
    text: "Village, verrerie, secteur Marineland ou transfert aéroport — réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Biot 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Biot : transfert aéroport de Nice (~20 min), village perché, verrerie et secteur Marineland. Chauffeurs bilingues, 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/biot",
  },
};

export default biot;
